import { useState } from "react";
import { BoxTrack } from "@core/components/BoxTrack";
import { Stepper } from "@core/components/Stepper";
import { Value } from "@core/components/Value";
import type { Operation } from "@core/api/types";
import { useSm, type LossesView } from "./view";

type Track = "lp" | "fo";
type Kind = LossesView["kinds"][number];

const titles: Record<Track, string> = { lp: "Lebenspunkte", fo: "Fokus" };

/** Applies one operation at a time; buttons stay disabled while it is in flight. */
function useBusyApply() {
  const { apply } = useSm();
  const [busy, setBusy] = useState(false);
  const run = async (operation: Operation) => {
    if (busy) return;
    setBusy(true);
    try {
      await apply(operation);
    } finally {
      setBusy(false);
    }
  };
  return { busy, run };
}

function signed(penalty: number) {
  return penalty < 0 ? `−${-penalty}` : String(penalty);
}

/** Life or focus points to strike off by loss kind, heaviest first (GRW S. 14). */
export function LossCard({ track }: { track: Track }) {
  const { view, value } = useSm();
  const { busy, run } = useBusyApply();
  const losses = view.losses;
  const life = track === "lp";
  const data = life ? losses.life : losses.focus;
  const [kindId, setKindId] = useState(losses.kinds[0]?.id ?? "");
  const [amount, setAmount] = useState(1);
  const label = (k: Kind) => (life ? k.lifeName : k.name);
  const kind = losses.kinds.find((k) => k.id === kindId);
  const target = losses.kinds.find((k) => k.id === kind?.endsAs);
  const struck = kind ? (data.counts[kind.id] ?? 0) : 0;

  const level = value(losses.life.level)?.number;
  const perRow = value(life ? losses.life.perRow : data.boxes)?.number ?? 0;
  const rows = life
    ? losses.life.levels.map((l) => ({ boxes: perRow, label: `${l.name} ${signed(value(l.penalty)?.number ?? 0)}`, current: l.level === level }))
    : [{ boxes: perRow }];
  const current = losses.life.levels.find((l) => l.level === level);
  const marks = losses.kinds.map((k) => ({ id: k.id, symbol: k.symbol, label: label(k), count: data.counts[k.id] ?? 0 }));
  const marked = marks.reduce((sum, m) => sum + m.count, 0);

  return (
    <section className="card stack small-gap loss-card" aria-label={titles[track]}>
      <div className="row spread">
        <h3>{titles[track]}</h3>
        <Value valueKey={data.available} label="verfügbar" showLabel />
      </div>
      {life && (
        <div className="row small">
          <span>{current?.name ?? "–"}</span>
          <Value valueKey={losses.life.penalty} label="Wundabzug" showLabel />
        </div>
      )}
      <BoxTrack
        label={titles[track]}
        rows={rows}
        marks={marks}
        onTap={(tap) => {
          const change = tap.mark ? tap.markIndex - tap.mark.count : tap.index + 1 - marked;
          const id = tap.mark?.id ?? kind?.id;
          if (id && change !== 0) void run({ type: "sm.markLoss", payload: { track, kind: id, change } });
        }}
        boxLabel={(tap) =>
          tap.mark ? `Kästchen ${tap.index + 1}: ${tap.mark.label}, ab hier zurücknehmen` : `Kästchen ${tap.index + 1}: frei, bis hier ${kind ? label(kind) : ""} streichen`
        }
      />
      <div className="loss-kinds" role="radiogroup" aria-label="Art des Verlusts">
        {losses.kinds.map((k) => (
          <button
            key={k.id}
            type="button"
            role="radio"
            aria-checked={k.id === kindId}
            className={k.id === kindId ? "selected" : ""}
            title={k.summary ?? undefined}
            onClick={() => setKindId(k.id)}
          >
            <span className="loss-symbol" aria-hidden="true">
              {k.symbol}
            </span>
            <span>{label(k)}</span>
            <span className="muted small">{data.counts[k.id] ?? 0}</span>
          </button>
        ))}
      </div>
      {kind && (
        <div className="actions loss-actions">
          <Stepper value={amount} min={1} label="Anzahl" onChange={setAmount} />
          <button type="button" className="button primary" disabled={busy} onClick={() => void run({ type: "sm.markLoss", payload: { track, kind: kind.id, change: amount } })}>
            Streichen
          </button>
          <button type="button" className="button secondary" disabled={busy || struck < amount} onClick={() => void run({ type: "sm.markLoss", payload: { track, kind: kind.id, change: -amount } })}>
            Zurück
          </button>
          {target && (
            <button
              type="button"
              className="button secondary"
              disabled={busy || struck < amount}
              onClick={() => void run({ type: "sm.endLoss", payload: { track, kind: kind.id, amount } })}
            >
              Wird {label(target)}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

/** Breather and rest; a rest restores what a breather does and regenerates consumed points (GRW S. 173–174, 196). */
export function RecoveryActions() {
  const { view } = useSm();
  const { busy, run } = useBusyApply();
  const { kinds, life, focus, rest } = view.losses;
  const struck = (track: Track, kind: string) => ((track === "lp" ? life : focus).counts[kind] ?? 0) > 0;
  const breather = kinds.filter((k) => k.breather).some((k) => struck("lp", k.id) || struck("fo", k.id));
  const restorable = breather || rest.some((r) => struck(r.track, r.kind));
  return (
    <section className="card stack small-gap" aria-label="Erholung">
      <div className="actions">
        <button type="button" className="button secondary" disabled={busy || !breather} onClick={() => void run({ type: "sm.breather", payload: {} })}>
          Verschnaufpause
        </button>
        <button type="button" className="button secondary" disabled={busy || !restorable} onClick={() => void run({ type: "sm.rest", payload: {} })}>
          Ruhephase
        </button>
      </div>
      {rest.length > 0 && (
        <>
          <p className="muted small recovery-note">
            Ruhephase zusätzlich:{" "}
            {rest.map((r) => (
              <Value key={r.id} valueKey={r.value} label={titles[r.track]} showLabel />
            ))}{" "}
            zurück
          </p>
          <p className="muted small">
            {rest.map((r) => `${r.summary ?? r.name} (GRW S. ${r.source.page})`).join(" ")}
          </p>
        </>
      )}
    </section>
  );
}
